/**
 * Copyright (c) 2019-Present Syncari - All rights reserved.
 */
import cx from 'classnames';
import * as React from 'react';

import type { FormFieldProps } from '../../types';
import Icon from '../Icon/Icon';
import Tooltip from '../Tooltip/Tooltip';
import './FormField.less';

let _formFieldIdCounter = 0;

const FormField = ({ label, required, tooltip, helpText, error, htmlFor, className, children }: FormFieldProps) => {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const autoId = React.useMemo(() => `ds-form-field-${++_formFieldIdCounter}`, []);
  const childProps = children.props as { id?: string };

  const resolvedId = htmlFor ?? childProps.id ?? autoId;

  // Inject id into the child if it doesn't already have one (and no explicit htmlFor).
  const child = childProps.id ? children : React.cloneElement(children, { id: resolvedId });

  const showError = Boolean(error);

  return (
    <div className={cx('form-field', { 'form-field--invalid': showError }, className)}>
      {(label || tooltip) && (
        <div className="form-field__label-row">
          {label && (
            <label className="form-field__label" htmlFor={resolvedId}>
              {label}
              {required && (
                <span className="form-field__required" aria-hidden="true">
                  *
                </span>
              )}
            </label>
          )}
          {tooltip && (
            <Tooltip content={tooltip}>
              <span tabIndex={0} aria-label={tooltip}>
                <Icon name="miscellaneous/help" size="large" />
              </span>
            </Tooltip>
          )}
        </div>
      )}
      <div className="form-field__input">{child}</div>
      {showError ? (
        <div className="form-field__error" role="alert">
          {error}
        </div>
      ) : helpText ? (
        <div className="form-field__help">{helpText}</div>
      ) : null}
    </div>
  );
};

FormField.displayName = 'FormField';

export default FormField;
